import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronRight,
  ChevronsUpDown,
  ListTree, // fork(oem): platform map entry
  LogOut,
  Monitor,
  Moon,
  Settings,
  ShieldCheck,
  Sun,
} from 'lucide-react';
import { Commentable } from '@/shared/comments/Commentable';
import { useTheme } from '@/app/providers';
import { cn } from '@/shared/cn';
import {
  MENU_CONTENT,
  MENU_ITEM_BASE,
  MENU_SUB_CONTENT,
} from '@/shared/ui-class';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { SEED_USER } from '@/versions/v1/shared/user-seed';
// fork(oem):
import { useBrand } from '@/versions/v1/shared/BrandContext';
import { orgLabel, useOemPersona } from '@/versions/v1/shared/OemPersonaContext';
import { BrandMenuItem } from '@/versions/v1/shared/BrandMenuItem'; // fork(oem): client selector
import { DemoModeMenuItem } from '@/versions/v1/shared/DemoModeMenuItem'; // fork(oem): executive rail toggle
import { TourMenuItem } from '@/versions/v1/shared/TourMenuItem'; // fork(oem): guided tour

const themeOptions = [
  { value: 'light' as const, label: 'Light', Icon: Sun },
  { value: 'dark' as const, label: 'Dark', Icon: Moon },
  { value: 'system' as const, label: 'System', Icon: Monitor },
];

type ThemeValue = (typeof themeOptions)[number]['value'];

const AVATAR_CLASSES =
  'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-zinc-200 text-sm font-semibold text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200';

function initialOf(name: string, email: string): string {
  const source = (name || email || '?').trim();
  return source.charAt(0).toUpperCase() || '?';
}

export function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  onOutside: () => void,
  enabled: boolean,
) {
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onOutside();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onOutside, enabled]);
}

function DropdownPanel({
  children,
  className,
  align = 'start',
  side = 'top',
}: {
  children: ReactNode;
  className?: string;
  align?: 'start' | 'end';
  side?: 'top' | 'right';
}) {
  return (
    <div
      className={cn(
        MENU_CONTENT,
        'absolute z-50',
        side === 'right'
          ? 'bottom-0 left-full ml-1.5'
          : cn('bottom-full mb-1.5', align === 'end' ? 'right-0' : 'left-0'),
        className,
      )}
      role="menu"
    >
      {children}
    </div>
  );
}

function ThemeSubmenu({
  open,
  anchorRef,
  panelRef,
  currentTheme,
  onSelect,
}: {
  open: boolean;
  anchorRef: React.RefObject<HTMLButtonElement>;
  panelRef: React.Ref<HTMLDivElement>;
  currentTheme: ThemeValue;
  onSelect: (value: ThemeValue) => void;
}) {
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const update = () => {
      const rect = anchorRef.current!.getBoundingClientRect();
      setCoords({ top: rect.top, left: rect.right + 6 });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={panelRef}
      className={cn(MENU_SUB_CONTENT, 'fixed z-[200] w-[148px]')}
      style={{ top: coords.top, left: coords.left }}
      role="menu"
    >
      {themeOptions.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          className="flex w-full cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm text-zinc-700 outline-none hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
          onClick={() => onSelect(value)}
        >
          <Icon className="h-4 w-4 text-zinc-500" />
          {label}
          {currentTheme === value ? (
            <Check className="ml-auto h-3.5 w-3.5 text-zinc-500" />
          ) : null}
        </button>
      ))}
    </div>,
    document.body,
  );
}

export function AccountMenu({
  variant = 'full',
}: {
  variant?: 'full' | 'compact';
}) {
  const navigate = useNavigate();
  const { can } = useRole();
  const { themePreference, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [themeSubOpen, setThemeSubOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const themeTriggerRef = useRef<HTMLButtonElement>(null);
  const themeSubRef = useRef<HTMLDivElement>(null);
  const brandSubRef = useRef<HTMLDivElement>(null); // fork(oem): brand submenu portal

  // fork(oem): the sidebar footer identifies the active OEM+ persona, not the
  // foundation's seed user — the demo is always "viewing as" one of six
  // aftermarket roles, and the footer is where you read which. `SEED_USER`
  // still supplies the email, which the persona seed has no field for.
  const { persona } = useOemPersona();
  const { brand } = useBrand();
  const { email } = SEED_USER;
  const display = persona.name;
  const subtitle = orgLabel(persona, brand.lockup);
  const initial = initialOf(display, email);
  const isAdmin = can('roles:admin');

  const close = useCallback(() => {
    setOpen(false);
    setThemeSubOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (themeSubRef.current?.contains(target)) return;
      if (brandSubRef.current?.contains(target)) return; // fork(oem)
      close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, close]);

  const currentTheme: ThemeValue = themePreference;

  const menuBody = (
    <>
      {isAdmin ? (
        <Link
          to="/admin"
          className={MENU_ITEM_BASE}
          role="menuitem"
          onClick={close}
        >
          <ShieldCheck className="h-4 w-4 text-zinc-500" />
          Admin
        </Link>
      ) : null}

      <Link
        to="/settings"
        className={MENU_ITEM_BASE}
        role="menuitem"
        onClick={close}
      >
        <Settings className="h-4 w-4 text-zinc-500" />
        Settings
      </Link>

      {/* fork(oem/v1.8): the platform's section-by-section outline. Help is off
          the executive rail, so this is how a presenter reaches the map there. */}
      <Link
        to="/help/structure"
        className={MENU_ITEM_BASE}
        role="menuitem"
        onClick={close}
      >
        <ListTree className="h-4 w-4 text-zinc-500" />
        Platform map
      </Link>

      <div>
        <button
          ref={themeTriggerRef}
          type="button"
          className="flex w-full cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm text-zinc-700 outline-none hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
          onClick={() => setThemeSubOpen((v) => !v)}
        >
          <CurrentThemeIcon themeValue={currentTheme} />
          Theme
          <span className="ml-auto text-xs text-muted">
            {themeOptions.find((o) => o.value === currentTheme)?.label}
          </span>
          <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />
        </button>
        <ThemeSubmenu
          open={themeSubOpen}
          anchorRef={themeTriggerRef}
          panelRef={themeSubRef}
          currentTheme={currentTheme}
          onSelect={(value) => {
            setTheme(value);
            close();
          }}
        />
      </div>

      {/* fork(oem): client brand selector, beside Theme */}
      <BrandMenuItem onSelected={close} panelRef={brandSubRef} />

      {/* fork(oem/P7a A6): executive-rail toggle, so the full build is one
          click away from wherever the presenter already is. */}
      <DemoModeMenuItem />
      <TourMenuItem onSelected={close} /> {/* fork(oem): start the 17-stop guided tour */}

      <div className="my-1 h-px bg-zinc-200 dark:bg-zinc-800" />

      <button
        type="button"
        className="flex w-full cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm text-red-600/80 outline-none transition-colors hover:text-red-700 focus:bg-red-500/5 focus:text-red-700 dark:text-red-500/80 dark:hover:text-red-400 dark:focus:bg-red-500/10"
        onClick={() => {
          close();
          navigate('/login?reason=session-ended');
        }}
      >
        <LogOut className="h-4 w-4" strokeWidth={1.6} />
        Sign out
      </button>
    </>
  );

  if (variant === 'compact') {
    return (
      <Commentable name="AccountMenu">
        <div ref={rootRef} className="relative">
          <button
            type="button"
            aria-label={`${display} account menu`}
            title={display}
            className="rounded-full transition-colors hover:bg-zinc-900/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 dark:hover:bg-white/5"
            onClick={() => setOpen((v) => !v)}
          >
            <span className={AVATAR_CLASSES}>{initial}</span>
          </button>
          {open ? (
            <DropdownPanel className="w-[228px]" side="right">
              {menuBody}
            </DropdownPanel>
          ) : null}
        </div>
      </Commentable>
    );
  }

  return (
    <Commentable name="AccountMenu">
      <div ref={rootRef} className="relative">
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-zinc-900/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 dark:hover:bg-white/5"
          onClick={() => setOpen((v) => !v)}
        >
          <span className={AVATAR_CLASSES}>{initial}</span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
              {display}
            </span>
            {/* fork(oem): persona org + client lockup in place of the email. */}
            <span className="block truncate text-xs text-muted">{subtitle}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 flex-shrink-0 text-muted" strokeWidth={1.6} />
        </button>
        {open ? (
          <DropdownPanel className="w-[228px]">{menuBody}</DropdownPanel>
        ) : null}
      </div>
    </Commentable>
  );
}

function CurrentThemeIcon({ themeValue }: { themeValue: ThemeValue }) {
  const Icon =
    themeValue === 'dark' ? Moon : themeValue === 'light' ? Sun : Monitor;
  return <Icon className="h-4 w-4 text-zinc-500" />;
}
